import { Box, Grid, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { HormoneSnapshot } from '../../types';
import cortisol_reference from '../../config/cortisol_reference.json';
import { colors } from '../../utils/theme';
import { MIN_TO_MILLIS, roundNumber } from '../../utils';

interface CustomTickProps {
  x: number;
  y: number;
  payload: {
    value: string;
  };
}

const REFERENCE_LINE = cortisol_reference.data;
const REFERENCE_RANGE = cortisol_reference.reference_range;
const MAX_Y_RANGE = 30;
const MIN_Y_RANGE = 15;

export const StressInsightsLineChart: React.FC<{
  snapshot: HormoneSnapshot;
}> = ({ snapshot }) => {
  const [snapshotIdx, setSnapshotIdx] = useState<number>(0);

  const referenceData = REFERENCE_LINE.map((point, i) => {
    const range = REFERENCE_RANGE[i];
    return {
      time: point.x,
      refValue: point.y,
      optimal_range: [range.min, range.max],
    };
  });

  // Expectation is waking time is 30 min before the first sample
  const wakingTime =
    new Date(snapshot?.test_results[0].sample_datetime).getTime() -
    30 * MIN_TO_MILLIS;

  const sampleData = snapshot?.test_results.map((testResult) => {
    const sampleTime = new Date(testResult.sample_datetime).getTime();
    const hoursSinceWaking = (sampleTime - wakingTime) / (60 * MIN_TO_MILLIS);

    return {
      time: hoursSinceWaking,
      sampleValue: testResult.result,
    };
  });

  const xMin = sampleData[0].time - 0.5;
  const xMax = sampleData[sampleData.length - 1].time;

  // Domain of x values
  const xDomain = [xMin, xMax];

  // Filter the reference data to use only within the domain values,
  // and then sort for the chart usage
  const chartData = [
    ...referenceData.filter(({ time }) => time >= xMin && time <= xMax),
    ...sampleData,
    // ...medianLineData,
  ].sort((a, b) => a.time - b.time);

  const highestResult = Math.max(
    ...(snapshot?.test_results.map((r) => r.result) || [])
  );

  const yRange =
    highestResult <= MIN_Y_RANGE
      ? MIN_Y_RANGE
      : Math.min(Math.ceil(highestResult), MAX_Y_RANGE);

  const tickLabels = ['+30min from waking', 'Mid-day', 'Night'];

  const tickFormatter = (time: number) => {
    const index = sampleData.findIndex((sample) => sample.time === time);
    return tickLabels[index] || '';
  };

  const CustomXTick: React.FC<CustomTickProps> = ({ x, y, payload }) => (
    <g transform={`translate(${x},${y})`}>
      <foreignObject
        x={-12}
        y={0}
        width={140}
        height={20}
        style={{ overflow: 'hidden' }}
      >
        <Typography
          variant="light"
          fontSize={12}
          sx={{ color: colors.greys.GREY_600 }}
        >
          {tickFormatter(Number(payload.value))}
        </Typography>
      </foreignObject>
    </g>
  );

  const CustomYTick: React.FC<CustomTickProps> = ({ x, y, payload }) => (
    <g transform={`translate(${x},${y})`}>
      <foreignObject x={-12} y={-12} width={30} height={20}>
        <Typography
          variant="light"
          fontSize={12}
          sx={{ color: colors.greys.GREY_600 }}
        >
          {payload.value}
        </Typography>
      </foreignObject>
    </g>
  );

  const CustomTooltip: React.FC<any> = ({ active, payload, label }) => {
    if (!(active && payload && payload.length) || label === 0) {
      return null;
    }

    return (
      <Box
        alignSelf="right"
        paddingRight={1}
        sx={{
          backgroundColor: colors.greys.BASE_WHITE,
          border: `1px solid ${colors.greys.BORDER_GREY}`,
          borderRadius: 1.25,
          boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
        }}
      >
        <Stack spacing={0.5} paddingBottom={2}>
          <Typography fontSize={14} sx={{ padding: 2 }}>
            {`Hours from waking: ${roundNumber(label)}`}
          </Typography>
          {payload.map((p: any, i: number) => {
            let value = p.value;
            if (Array.isArray(p.value)) {
              value = `${roundNumber(p.value[0])} - ${roundNumber(p.value[1])}`;
            } else {
              value = roundNumber(value);
            }
            return (
              <Typography key={i} color={p.color} fontSize={12} sx={{ px: 2 }}>
                {`${p.name}: ${value}`}
              </Typography>
            );
          })}
        </Stack>
      </Box>
    );
  };

  return (
    <Grid container justifyContent="flex-start" sx={{ ml: -2.5 }}>
      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={chartData}>
          <CartesianGrid vertical={false} stroke={colors.greys.BORDER_GREY} />
          <XAxis
            id="xAxis"
            dataKey="time"
            type="number"
            domain={xDomain}
            tickFormatter={tickFormatter}
            ticks={sampleData.map((data) => data.time)}
            tick={(props) => <CustomXTick {...props} />}
            tickLine={false}
            axisLine={{ stroke: colors.greys.BORDER_GREY }}
          />
          <YAxis
            id="yAxis"
            type="number"
            domain={[0, yRange]}
            tickCount={6}
            axisLine={false}
            tickLine={false}
            tick={(props) => <CustomYTick {...props} />}
          />
          <Area
            type="monotone"
            dataKey="optimal_range"
            opacity={0.5}
            stroke={colors.primarys.GREEN}
            fill={colors.primarys.GREEN}
            connectNulls
            name="Optimal Range"
          />
          <Line
            type="monotone"
            dataKey="refValue"
            name="Median"
            strokeWidth={1}
            connectNulls
            stroke={colors.primarys.GREEN}
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="sampleValue"
            name="Samples"
            strokeWidth={2}
            connectNulls
            stroke={colors.greys.BRAND_BLACK}
            dot={{ stroke: colors.greys.BRAND_BLACK, strokeWidth: 1 }}
          />
          <Tooltip content={<CustomTooltip />} offset={20} />
        </ComposedChart>
      </ResponsiveContainer>
    </Grid>
  );
};
